import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { Container, Input, Modal, ModalBody } from 'reactstrap';
import { theme } from 'styled-tools';
import styled from 'styled-components/macro';
import {
  SEND_MOBILE_VERIFICATION_CODE_MUTATION,
  VERIFY_MOBILE_VERIFICATION_CODE_MUTATION,
} from 'hooks/mutations/graphql';
import Alert from 'components/Alerts';
import { Button } from 'components/Button';
import { formatMobileNumberForDisplay } from 'utils/mobileNumberValidation';

const ResendButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  color: ${theme('colors.primary')};
  text-decoration: underline;

  &:hover {
    color: ${theme('colors.black')};
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledInput = styled(Input)`
  width: 60%;
`;

const StyledContainer = styled(Container)`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const MaskedPhoneNumber = styled.p`
  margin-bottom: 1.5rem;
  font-weight: bold;
  color: ${theme('colors.primary')};
`;

const ModalTitle = styled.h2`
  color: ${theme('colors.primary')};
  text-align: center;
  font-weight: 700;
`;

function MobileNumberVerificationModal({ isOpen, onClose, mobileNumber, onVerificationSuccess, onCustomVerify }) {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState(null);
  const [verifyMobileVerificationCode, { loading: verifyLoading }] = useMutation(
    VERIFY_MOBILE_VERIFICATION_CODE_MUTATION
  );
  const [sendMobileVerificationCode, { loading: sendLoading }] = useMutation(SEND_MOBILE_VERIFICATION_CODE_MUTATION);

  const handleVerify = async () => {
    if (onCustomVerify) {
      await onCustomVerify({ mobileNumber, verificationCode, setError });
    } else {
      const { data } = await verifyMobileVerificationCode({
        variables: {
          mobileNumber,
          code: verificationCode,
        },
      });

      const result = data?.verifyMobileCode;

      if (result.success) {
        onVerificationSuccess();
        onClose();
      } else {
        setError(result.message);
      }
    }
  };

  const handleResendCode = async () => {
    const { data } = await sendMobileVerificationCode({
      variables: {
        mobileNumber: mobileNumber,
      },
    });

    const result = data?.sendMobileVerificationCode;

    if (result?.success) {
      setError(null);
    } else {
      setError(result?.message || 'Failed to resend verification code');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalBody>
        <Container>
          {error && <Alert type='error' alertText={error} />}
          <ModalTitle>Phone Verification</ModalTitle>

          <p
            style={{
              textAlign: 'center',
              marginBottom: '0.5rem',
              fontSize: '1rem',
              color: theme('colors.text'),
              paddingTop: error ? '1rem' : '1rem',
            }}
          >
            A verification code has been sent to your phone.&nbsp;
            <MaskedPhoneNumber>{formatMobileNumberForDisplay(mobileNumber)}</MaskedPhoneNumber>
          </p>

          <StyledContainer>
            <StyledInput
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value)}
              placeholder='Enter your one-time code'
              autoFocus
            />

            <Button
              kind='primary'
              onClick={handleVerify}
              disabled={verificationCode.length !== 6 || verifyLoading}
              style={{ width: '60%', marginTop: '2rem' }}
            >
              {verifyLoading ? 'Verifying...' : 'Verify'}
            </Button>
          </StyledContainer>
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <ResendButton onClick={handleResendCode} disabled={sendLoading} type='button'>
              {sendLoading ? 'Sending...' : 'Resend code'}
            </ResendButton>
          </div>
        </Container>
      </ModalBody>
    </Modal>
  );
}

export default MobileNumberVerificationModal;
